import React, { useEffect, useMemo, useState } from "react";
import {
  getPaperAutoTradeAccount,
  getPaperAutoTradeOrders,
  getPaperAutoTradePositions,
  submitPaperAutoTradeOrder,
} from "../paperAutoTrade/paperAutoTradeApi";
import PaperAutoTradeChart from "./PaperAutoTradeChart";
import "./PaperAutoTradeWorkspace.css";

const STRATEGIES = ["Day Trading", "Premarket", "Swing", "Options"];
const SERVER_MAX_POSITIONS = 3;
const SERVER_MAX_DAILY_LOSS = 250;
const SERVER_MAX_RISK_PER_TRADE = 50;

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const roundPrice = (value) => Number(value.toFixed(2));

function normalizeSymbol(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.-]/g, "")
    .slice(0, 8);
}

function sharedTradePlanToSetup(sharedTradePlan) {
  const symbol = normalizeSymbol(sharedTradePlan?.symbol);
  const currentPrice = Number(sharedTradePlan?.currentPrice);
  const entryPrice = Number(sharedTradePlan?.entry);
  const stopLoss = Number(sharedTradePlan?.stop);
  const profitTarget = Number(sharedTradePlan?.targets?.target1);
  const profitTarget2 = Number(sharedTradePlan?.targets?.target2);
  const profitTarget3 = Number(sharedTradePlan?.targets?.target3);
  const positionSize = Number(sharedTradePlan?.quantity);

  if (!symbol || !Number.isFinite(currentPrice) || currentPrice <= 0) {
    return null;
  }

  return {
    symbol,
    estimatedPrice: currentPrice,
    entryPrice:
      Number.isFinite(entryPrice) && entryPrice > 0 ? roundPrice(entryPrice) : "",
    stopLoss:
      Number.isFinite(stopLoss) && stopLoss > 0 ? roundPrice(stopLoss) : "",
    profitTarget:
      Number.isFinite(profitTarget) && profitTarget > 0
        ? roundPrice(profitTarget)
        : "",
    profitTarget2:
      Number.isFinite(profitTarget2) && profitTarget2 > 0
        ? roundPrice(profitTarget2)
        : "",
    profitTarget3:
      Number.isFinite(profitTarget3) && profitTarget3 > 0
        ? roundPrice(profitTarget3)
        : "",
    positionSize:
      Number.isSafeInteger(positionSize) && positionSize > 0 ? positionSize : "",
  };
}

function emptySetup(symbol = "AAPL") {
  return {
    symbol,
    estimatedPrice: "",
    entryPrice: "",
    stopLoss: "",
    profitTarget: "",
    profitTarget2: "",
    profitTarget3: "",
    positionSize: "",
  };
}

function PaperAutoTradeWorkspace({ sharedTradePlan = null }) {
  const initialSharedSetup = sharedTradePlanToSetup(sharedTradePlan);

  const [symbolInput, setSymbolInput] = useState(
    initialSharedSetup?.symbol || "AAPL",
  );
  const [strategy, setStrategy] = useState("Day Trading");
  const [direction, setDirection] = useState("Long");
  const [setup, setSetup] = useState(
    () => initialSharedSetup || emptySetup("AAPL"),
  );
  const [setupNotice, setSetupNotice] = useState(
    "Waiting for an authenticated trade plan from Live Trading.",
  );

  const [brokerAccount, setBrokerAccount] = useState(null);
  const [brokerPositions, setBrokerPositions] = useState([]);
  const [brokerOpenOrders, setBrokerOpenOrders] = useState([]);
  const [brokerClosedOrders, setBrokerClosedOrders] = useState([]);
  const [brokerStateStatus, setBrokerStateStatus] = useState(
    "Loading Alpaca PAPER account state.",
  );
  const [brokerRefreshKey, setBrokerRefreshKey] = useState(0);
  const [isSubmittingPaperEntry, setIsSubmittingPaperEntry] = useState(false);
  const [paperEntryStatus, setPaperEntryStatus] = useState(
    "No Alpaca PAPER entry submitted from this workspace yet.",
  );

  useEffect(() => {
    const nextSharedSetup = sharedTradePlanToSetup(sharedTradePlan);
    if (!nextSharedSetup) return;

    setSymbolInput(nextSharedSetup.symbol);
    setSetup(nextSharedSetup);
    setSetupNotice(
      `${nextSharedSetup.symbol} authenticated plan loaded from Live Trading.`,
    );
  }, [sharedTradePlan]);

  useEffect(() => {
    let cancelled = false;

    async function loadBrokerState() {
      setBrokerStateStatus("Loading Alpaca PAPER account state.");

      try {
        const [
          accountResponse,
          positionsResponse,
          openOrdersResponse,
          closedOrdersResponse,
        ] = await Promise.all([
          getPaperAutoTradeAccount(),
          getPaperAutoTradePositions(),
          getPaperAutoTradeOrders("open", 100),
          getPaperAutoTradeOrders("closed", 100),
        ]);

        if (cancelled) return;

        setBrokerAccount(accountResponse?.data || null);
        setBrokerPositions(
          Array.isArray(positionsResponse?.data) ? positionsResponse.data : [],
        );
        setBrokerOpenOrders(
          Array.isArray(openOrdersResponse?.data) ? openOrdersResponse.data : [],
        );
        setBrokerClosedOrders(
          Array.isArray(closedOrdersResponse?.data)
            ? closedOrdersResponse.data
            : [],
        );
        setBrokerStateStatus("Alpaca PAPER account connected.");
      } catch (error) {
        if (cancelled) return;

        setBrokerAccount(null);
        setBrokerPositions([]);
        setBrokerOpenOrders([]);
        setBrokerClosedOrders([]);
        setBrokerStateStatus(
          `Alpaca PAPER account state unavailable: ${error.message}`,
        );
      }
    }

    loadBrokerState();

    return () => {
      cancelled = true;
    };
  }, [brokerRefreshKey]);

  const estimatedMaximumLoss = useMemo(() => {
    const entry = Number(setup.entryPrice);
    const stop = Number(setup.stopLoss);
    const quantity = Number(setup.positionSize);

    if (
      !Number.isFinite(entry) ||
      !Number.isFinite(stop) ||
      !Number.isFinite(quantity) ||
      entry <= 0 ||
      stop <= 0 ||
      quantity <= 0
    ) {
      return 0;
    }

    return Math.abs(entry - stop) * quantity;
  }, [setup.entryPrice, setup.positionSize, setup.stopLoss]);

  const riskPerShare = Math.abs(
    Number(setup.entryPrice) - Number(setup.stopLoss),
  );
  const rewardPerShare = Math.abs(
    Number(setup.profitTarget) - Number(setup.entryPrice),
  );
  const riskReward =
    riskPerShare > 0 && Number.isFinite(rewardPerShare)
      ? `${(rewardPerShare / riskPerShare).toFixed(2)} : 1`
      : "—";

  const loadSetup = () => {
    const cleanSymbol = normalizeSymbol(symbolInput);

    if (!cleanSymbol) {
      setSymbolInput(setup.symbol);
      setSetupNotice("Enter a valid symbol before loading an authenticated plan.");
      return;
    }

    const nextSetup = sharedTradePlanToSetup(sharedTradePlan);
    if (!nextSetup || nextSetup.symbol !== cleanSymbol) {
      setSetupNotice(
        `${cleanSymbol} authenticated trade plan is not available. Analyze this symbol in Live first.`,
      );
      return;
    }

    setSetup(nextSetup);
    setSymbolInput(cleanSymbol);
    setSetupNotice(
      `${cleanSymbol} authenticated plan loaded. Review every level before Paper Auto execution is enabled.`,
    );
  };

  const changeDirection = (nextDirection) => {
    setDirection(nextDirection);
    setSetup((current) => ({
      ...current,
      entryPrice: "",
      stopLoss: "",
      profitTarget: "",
      profitTarget2: "",
      profitTarget3: "",
      positionSize: "",
    }));
    setSetupNotice(
      `Direction changed to ${nextDirection}. Re-analyze ${setup.symbol} in Live Trading to load a matching authenticated plan.`,
    );
  };

  const updateSetupField = (field, value) => {
    setSetup((current) => ({ ...current, [field]: value }));
  };

  const currentPlanWithinServerRisk =
    estimatedMaximumLoss > 0 &&
    estimatedMaximumLoss <= SERVER_MAX_RISK_PER_TRADE;

  const quantity = Number(setup.positionSize);
  const entryPrice = Number(setup.entryPrice);
  const stopPrice = Number(setup.stopLoss);
  const hasCompletePaperEntry =
    Number.isSafeInteger(quantity) &&
    quantity > 0 &&
    Number.isFinite(entryPrice) &&
    entryPrice > 0 &&
    Number.isFinite(stopPrice) &&
    stopPrice > 0;

  const directionRiskIsValid =
    direction === "Long"
      ? stopPrice < entryPrice
      : stopPrice > entryPrice;

  const canSubmitPaperEntry =
    Boolean(brokerAccount) &&
    hasCompletePaperEntry &&
    directionRiskIsValid &&
    currentPlanWithinServerRisk &&
    !isSubmittingPaperEntry;

  const submitPaperEntry = async () => {
    if (!canSubmitPaperEntry) return;

    setIsSubmittingPaperEntry(true);
    setPaperEntryStatus(
      `Submitting ${setup.symbol} entry to Alpaca PAPER for server-side risk validation.`,
    );

    try {
      const response = await submitPaperAutoTradeOrder({
        symbol: setup.symbol,
        quantity,
        side: direction === "Long" ? "buy" : "sell",
        order_type: "limit",
        time_in_force: "day",
        limit_price: entryPrice,
        stop_price: stopPrice,
        take_profit_price: Number(setup.profitTarget),
      });

      const order = response?.data || {};
      const orderStatus = String(order.status || "submitted").toUpperCase();
      const orderId = order.id ? ` · Order ${order.id}` : "";

      setPaperEntryStatus(
        `Alpaca PAPER entry ${orderStatus} for ${setup.symbol}${orderId}. Stop and Target 1 are attached at Alpaca PAPER as a bracket order.`,
      );
      setBrokerRefreshKey((value) => value + 1);
    } catch (error) {
      setPaperEntryStatus(
        `Alpaca PAPER entry was not submitted: ${error.message}`,
      );
    } finally {
      setIsSubmittingPaperEntry(false);
    }
  };

  const timelineEvents = [
    {
      title: `Authenticated plan loaded for ${setup.symbol}`,
      detail: "Trade levels come from the shared Live Trading plan.",
      state: setup.entryPrice ? "complete" : "current",
    },
    {
      title: "Server risk preflight",
      detail:
        "The protected backend re-checks position count, daily loss, buying power, direction, and entry-to-stop risk before any Paper Auto order.",
      state: "pending",
    },
    {
      title: "Alpaca PAPER entry",
      detail:
        "A deliberate DAY limit entry can be submitted through the protected Paper Auto route after server-side risk checks. The protective stop and Target 1 are attached at Alpaca PAPER as bracket-order exits.",
      state: paperEntryStatus.startsWith("Alpaca PAPER entry ") &&
        !paperEntryStatus.includes("not submitted")
        ? "complete"
        : "pending",
    },
    {
      title: "Broker state refresh",
      detail:
        "Account, positions, and order state are read from authenticated Alpaca PAPER endpoints.",
      state: brokerAccount ? "complete" : "current",
    },
  ];

  return (
    <section
      className="paperAutoTradeWorkspace"
      aria-labelledby="paper-autotrade-title"
    >
      <div className="paperAutoTradeSafetyBanner" role="status">
        <span aria-hidden="true">!</span>
        PAPER AUTOTRADE · ALPACA PAPER ONLY · NO REAL-MONEY ORDERS
      </div>

      <header className="paperAutoTradeHeader">
        <div>
          <p className="paperAutoTradeEyebrow">
            PAPER ACCOUNT · BROKER-BACKED AUTOTRADE
          </p>
          <h1 id="paper-autotrade-title">Paper AutoTrade</h1>
          <p>
            Review an authenticated market plan alongside real Alpaca PAPER
            account state. Entry submission is broker-backed and protected by
            server-side Paper Auto risk checks.
          </p>
        </div>
        <div
          className="paperAutoTradeStatus status-monitoring"
          aria-label="Engine status: Alpaca PAPER"
        >
          Alpaca PAPER
        </div>
      </header>

      <div className="paperAutoTradePrimaryGrid">
        <div className="paperAutoTradeLeftColumn">
          <section
            className="paperAutoTradePanel paperAutoTradeSetupPanel"
            aria-labelledby="autotrade-setup-title"
          >
            <SectionHeading
              eyebrow="AUTHENTICATED MARKET INPUT"
              id="autotrade-setup-title"
              title="Symbol & Setup"
              detail="Authenticated market data"
            />

            <form
              className="paperAutoTradeSymbolLoader"
              onSubmit={(event) => {
                event.preventDefault();
                loadSetup();
              }}
            >
              <label>
                Symbol search
                <input
                  aria-label="Paper AutoTrade symbol"
                  value={symbolInput}
                  onChange={(event) =>
                    setSymbolInput(event.target.value.toUpperCase())
                  }
                  autoComplete="off"
                  maxLength="8"
                />
              </label>
              <button type="submit">Load Authenticated Plan</button>
            </form>

            <div className="paperAutoTradeSetupGrid">
              <label>
                Selected strategy
                <select
                  value={strategy}
                  onChange={(event) => setStrategy(event.target.value)}
                >
                  {STRATEGIES.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </label>

              <label>
                Authenticated market price
                <input
                  aria-label="Authenticated market price"
                  value={setup.estimatedPrice}
                  readOnly
                  aria-describedby="estimated-price-help"
                />
                <small id="estimated-price-help">
                  Authenticated market quote
                </small>
              </label>

              <fieldset className="paperAutoTradeDirection">
                <legend>Long/Short direction</legend>
                {["Long", "Short"].map((option) => (
                  <button
                    type="button"
                    key={option}
                    className={direction === option ? "active" : ""}
                    aria-pressed={direction === option}
                    onClick={() => changeDirection(option)}
                  >
                    {option}
                  </button>
                ))}
              </fieldset>

              <SetupInput
                label="Entry price"
                value={setup.entryPrice}
                onChange={(value) => updateSetupField("entryPrice", value)}
              />
              <SetupInput
                label="Stop loss"
                value={setup.stopLoss}
                onChange={(value) => updateSetupField("stopLoss", value)}
              />
              <SetupInput
                label="Profit target"
                value={setup.profitTarget}
                onChange={(value) => updateSetupField("profitTarget", value)}
              />
              <SetupInput
                label="Position size"
                value={setup.positionSize}
                step="1"
                min="1"
                onChange={(value) => updateSetupField("positionSize", value)}
              />

              <article className="paperAutoTradeMaxLoss">
                <span>Estimated maximum loss</span>
                <strong>{money(estimatedMaximumLoss)}</strong>
                <small>Entry-to-stop preview · server re-check required</small>
              </article>
            </div>

            <p className="paperAutoTradeNotice" role="status">
              {setupNotice}
            </p>
          </section>

          <section
            className="paperAutoTradePanel paperAutoTradeTicket"
            aria-labelledby="autotrade-ticket-title"
          >
            <SectionHeading
              eyebrow="PAPER EXECUTION PLAN"
              id="autotrade-ticket-title"
              title="AutoTrade Ticket"
              detail="Protected Alpaca PAPER entry"
            />

            <dl>
              <TicketRow label="Symbol" value={setup.symbol} />
              <TicketRow label="Strategy" value={strategy} />
              <TicketRow label="Direction" value={direction} />

              <div className="paperAutoTradeLevelGrid">
                <LevelBox label="ENTRY" value={setup.entryPrice} />
                <LevelBox label="STOP" value={setup.stopLoss} />
                <LevelBox label="TARGET 1" value={setup.profitTarget} />
                <LevelBox label="TARGET 2" value={setup.profitTarget2} />
                <LevelBox label="TARGET 3" value={setup.profitTarget3} />
              </div>

              <TicketRow
                label="Position size"
                value={
                  setup.positionSize
                    ? `${setup.positionSize} paper shares`
                    : "—"
                }
              />
              <TicketRow label="Risk/Reward" value={riskReward} />
              <TicketRow
                label="Execution status"
                value={
                  isSubmittingPaperEntry
                    ? "SUBMITTING TO ALPACA PAPER"
                    : "PAPER ENTRY READY"
                }
                status
              />
            </dl>

            <div className="paperAutoTradeActions">
              <button
                type="button"
                onClick={submitPaperEntry}
                disabled={!canSubmitPaperEntry}
              >
                {isSubmittingPaperEntry
                  ? "Submitting Alpaca PAPER Entry..."
                  : "Submit Alpaca PAPER Entry"}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setBrokerRefreshKey((value) => value + 1)}
              >
                Refresh Alpaca PAPER State
              </button>
            </div>

            <p className="paperAutoTradeNotice" role="status">
              {paperEntryStatus}
            </p>
            <p className="paperAutoTradeRiskNote">
              This submits only the entry order to Alpaca PAPER. The stop and Target 1 are submitted as broker-side bracket protection.
              Targets 2 and 3 remain planning levels until partial-exit management is implemented and verified.
            </p>
          </section>
        </div>

        <PaperAutoTradeChart
          symbol={setup.symbol}
          direction={direction}
          estimatedPrice={setup.estimatedPrice}
          entryPrice={setup.entryPrice}
          stopLoss={setup.stopLoss}
          profitTarget={setup.profitTarget}
        />
      </div>

      <section
        className="paperAutoTradePanel paperAutoTradeDecisionPanel"
        aria-labelledby="autotrade-nina-title"
      >
        <SectionHeading
          eyebrow="TRANSPARENT PAPER EXECUTION"
          id="autotrade-nina-title"
          title="AI Decision Panel"
          detail={`${setup.symbol} · authenticated market · Alpaca PAPER`}
        />

        <div className="paperAutoTradeDecisionHero">
          <div>
            <span>Plan source</span>
            <strong>Authenticated Live Trading</strong>
          </div>
          <div>
            <span>Current read</span>
            <strong>
              {direction} {strategy}
            </strong>
          </div>
        </div>

        <div className="paperAutoTradeDecisionDetails">
          <article>
            <h3>Why the AI Assistant is monitoring {setup.symbol}</h3>
            <p>
              The shared authenticated plan identifies a{" "}
              {direction.toLowerCase()} {strategy.toLowerCase()} setup near{" "}
              {money(setup.entryPrice)}. The browser does not manufacture a
              fill or position.
            </p>
          </article>

          <article>
            <h3>Risk explanation</h3>
            <p>
              Entry-to-stop risk is approximately{" "}
              {money(estimatedMaximumLoss)} for {setup.positionSize || 0} shares.
              The protected backend independently enforces the{" "}
              {money(SERVER_MAX_RISK_PER_TRADE)} Paper Auto per-trade cap.
            </p>
          </article>

          <article>
            <h3>Execution explanation</h3>
            <p>
              The entry order uses the protected Paper Auto route and is
              submitted only to Alpaca PAPER. The server re-checks the account
              risk limits before the provider call. Stop and target orders are
              not broker-attached yet.
            </p>
          </article>
        </div>

        <p className="paperAutoTradeRiskNote">
          The AI Assistant explains the authenticated plan and risk context.
          No live-money order routing is enabled.
        </p>
      </section>

      <section
        className="paperAutoTradePanel paperAutoTradeTimeline"
        aria-labelledby="autotrade-timeline-title"
      >
        <SectionHeading
          eyebrow="BROKER-BACKED WORKFLOW"
          id="autotrade-timeline-title"
          title="Paper Auto Timeline"
          detail="Broker-backed entry · no local fills"
        />

        <ol>
          {timelineEvents.map((event, index) => (
            <li className={event.state} key={event.title}>
              <i aria-hidden="true">{index + 1}</i>
              <div>
                <strong>{event.title}</strong>
                <p>{event.detail}</p>
              </div>
              <span>{event.state}</span>
            </li>
          ))}
        </ol>
      </section>

      <div className="paperAutoTradeSecondaryGrid">
        <section
          className="paperAutoTradePanel paperAutoTradeRiskControls"
          aria-labelledby="autotrade-risk-title"
        >
          <SectionHeading
            eyebrow="SERVER-ENFORCED PAPER SAFETY"
            id="autotrade-risk-title"
            title="Risk Controls"
            detail="Protected backend limits"
          />

          <div className="paperAutoTradeRiskGrid">
            <label>
              Maximum paper positions
              <input
                aria-label="Maximum paper positions"
                type="number"
                value={SERVER_MAX_POSITIONS}
                readOnly
              />
            </label>

            <label>
              Maximum daily paper loss
              <input
                aria-label="Maximum daily paper loss"
                type="number"
                value={SERVER_MAX_DAILY_LOSS}
                readOnly
              />
            </label>

            <label>
              Maximum risk per paper trade
              <input
                aria-label="Maximum risk per paper trade"
                type="number"
                value={SERVER_MAX_RISK_PER_TRADE}
                readOnly
              />
            </label>
          </div>

          <p className="paperAutoTradeRiskNote">
            These limits are enforced server-side before an Alpaca PAPER order
            can be submitted. Current plan preview:{" "}
            {estimatedMaximumLoss > 0
              ? currentPlanWithinServerRisk
                ? "within per-trade cap"
                : "over per-trade cap"
              : "waiting for complete plan levels"}
            .
          </p>
        </section>

        <section
          className="paperAutoTradePanel paperAutoTradeActivity"
          aria-labelledby="autotrade-activity-title"
        >
          <SectionHeading
            eyebrow="BROKER PAPER ACCOUNT"
            id="autotrade-activity-title"
            title="AutoTrade Activity"
            detail={brokerStateStatus}
          />

          <div className="paperAutoTradeSummary">
            <article>
              <span>Open Alpaca Paper Orders</span>
              <strong>{brokerOpenOrders.length}</strong>
            </article>
            <article>
              <span>Alpaca Paper Positions</span>
              <strong>{brokerPositions.length}</strong>
            </article>
            <article>
              <span>Closed Alpaca Paper Orders</span>
              <strong>{brokerClosedOrders.length}</strong>
            </article>
            <article>
              <span>Paper Equity</span>
              <strong>
                {brokerAccount?.equity != null
                  ? money(brokerAccount.equity)
                  : "—"}
              </strong>
            </article>
          </div>
        </section>
      </div>

      <footer className="paperAutoTradeFooter">
        Authenticated market data · Alpaca PAPER account source of truth · No
        live-money order routing
      </footer>
    </section>
  );
}

function SetupInput({
  label,
  value,
  onChange,
  min = "0.01",
  step = "0.01",
}) {
  return (
    <label>
      {label}
      <input
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function LevelBox({ label, value }) {
  return (
    <div className="paperAutoTradeLevelBox">
      <span>{label}</span>
      <strong>{value ? money(value) : "—"}</strong>
    </div>
  );
}

function TicketRow({ label, value, status = false }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={status ? "paperAutoTradeTicketStatus" : ""}>{value}</dd>
    </div>
  );
}

function SectionHeading({ eyebrow, id, title, detail }) {
  return (
    <div className="paperAutoTradeSectionHeading">
      <div>
        <span>{eyebrow}</span>
        <h2 id={id}>{title}</h2>
      </div>
      {detail && <small>{detail}</small>}
    </div>
  );
}

export default PaperAutoTradeWorkspace;
